import { Box, Button, FormControl, FormHelperText, Stack, TextField, Typography, useTheme } from '@mui/material';
import { useMemo } from 'react';
import { Controller, FormProvider } from 'react-hook-form';
import PhoneInput from 'react-phone-input-2';
import { useSelector } from 'react-redux';
import type { UserNode } from '/@/hooks/nodes';
import { useAdvisorRvLinks, useUserNodes } from '/@/hooks/nodes';
import { useIsValidatedAdvisor, useIsValidatedConsultant, useIsValidatedRV } from '/@/hooks/session';
import { _ } from '/@/locale';
import { MuiSelect } from '/@/mui/select';
import { getEnableRvAdvisorOnboarding, getSessionCountry } from '/@/store/session/selectors';
import AREA_CODES from '/@/utils/area-codes';
import { validatePhoneNumber } from '/@/utils/phone-numbers';
import { useRegisterSingleGrowerFormApi } from '../single-grower-form-context';
import { formatCuitDisplay } from './cuit-text-field';
import { useGrowerForm } from './use-grower-form';
import { validateGrowerEmail } from './validate-grower-email';

export { validateGrowerEmail } from './validate-grower-email';

export type GrowerFormValues = {
    segment: string;
    advisor: string;
    rv: string;
    name: string;
    location: string;
    email: string;
    phone: string;
};

const GrowerForm = ({
    singleGrower = false,
    cuitDisplay,
    mode = 'create',
    editNodeId,
    initialValues,
    onSubmit: onSubmitProp,
}: {
    cuitDisplay: string;
    singleGrower?: boolean;
    mode?: 'create' | 'edit';
    editNodeId?: number;
    initialValues?: Partial<GrowerFormValues>;
    onSubmit?: (data: GrowerFormValues, createdNode?: UserNode) => void | Promise<void>;
}) => {
    const isConsultant = useIsValidatedConsultant();
    const isAdvisor = useIsValidatedAdvisor();
    const isRV = useIsValidatedRV();
    const country = useSelector(getSessionCountry);
    const theme = useTheme();

    const { form, onSubmit, submitGrower, isValid, isSubmitting, ownUserNodeQuery } = useGrowerForm({
        cuitDisplay,
        mode,
        editNodeId,
        initialValues,
        onSubmit: onSubmitProp,
    });

    useRegisterSingleGrowerFormApi(singleGrower ? { isValid, isSubmitting, submit: submitGrower } : null);

    const segmentOptions: { value: string; label: string }[] = [
        { value: '', label: _('Seleccionar segmento') },
        { value: 'premium', label: _('Premium') },
        { value: 'common', label: _('Otros segmentos') },
    ];

    const growersQuery = useUserNodes({
        type: 'grower',
        page: 1,
        page_size: 10000,
        includeChildren: true,
    });

    const RVAdvisorLinksQuery = useAdvisorRvLinks();
    const advisorOptions = useMemo(() => {
        return (RVAdvisorLinksQuery.data?.results || []).map((link) => ({
            value: link.advisor.id.toString(),
            label: link.advisor.name,
        }));
    }, [RVAdvisorLinksQuery.data]);

    const rvNodesQuery = useUserNodes(
        { type: 'rv', page: 1, page_size: 10000, includeChildren: true },
        { enabled: isConsultant }
    );
    const rvOptions = useMemo(
        () => [
            { value: '', label: _('Seleccionar RV') },
            ...(rvNodesQuery.data?.results || []).map((rv) => ({
                value: rv.id.toString(),
                label: rv.name,
            })),
        ],
        [rvNodesQuery.data]
    );

    const isEditMode = mode === 'edit';
    const isSegmentDisabled = isEditMode && initialValues?.segment === 'premium';
    const enableRvAdvisorOnboarding = useSelector(getEnableRvAdvisorOnboarding);

    return (
        <FormProvider {...form}>
            <Stack
                p={2}
                alignItems='stretch'
                component='form'
                onSubmit={onSubmit}
                noValidate
                sx={{
                    width: '100%',
                    borderRadius: '8px',
                    border: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: '#F5F5F5',
                }}
                spacing={2}
            >
                <Stack spacing={0.5} alignItems='start'>
                    <Typography variant='subtitle1' fontWeight='bold' color='primary'>
                        {_('Paso 2 Cargar datos del productor')}
                    </Typography>
                    <Typography variant='subtitle1' color='primary.mui.inactive' sx={{ textAlign: 'left' }}>
                        {isEditMode
                            ? _('Editá los datos del productor con CUIT {{cuit}}:', { cuit: cuitDisplay })
                            : _('EL CUIT {{cuit}} no está registrado. Completá los datos del productor:', {
                                  cuit: formatCuitDisplay(cuitDisplay),
                              })}
                    </Typography>
                </Stack>

                {isAdvisor && (
                    <Controller
                        name='segment'
                        control={form.control}
                        rules={{ required: _('Seleccioná un segmento') }}
                        render={({ field, fieldState: { error } }) => (
                            <MuiSelect
                                inputLabel={_('Segmento')}
                                value={field.value}
                                onChange={(e) => field.onChange(e.target.value)}
                                onBlur={field.onBlur}
                                options={segmentOptions}
                                formControlProps={{
                                    fullWidth: true,
                                    size: 'small',
                                    error: !!error,
                                    disabled: isSegmentDisabled,
                                }}
                                formHelperTextProps={{ error: !!error }}
                                formHelperText={error?.message}
                                inputProps={{ sx: { background: 'white' } }}
                            />
                        )}
                    />
                )}

                {isRV && enableRvAdvisorOnboarding && advisorOptions.length > 0 && (
                    <Controller
                        name='advisor'
                        control={form.control}
                        render={({ field, fieldState: { error } }) => (
                            <MuiSelect
                                inputLabel={_('Asesor')}
                                value={field.value}
                                onChange={(e) => field.onChange(e.target.value)}
                                onBlur={field.onBlur}
                                options={advisorOptions}
                                formControlProps={{ fullWidth: true, size: 'small', error: !!error }}
                                formHelperTextProps={{ error: !!error }}
                                formHelperText={error?.message}
                                inputProps={{ sx: { background: 'white' } }}
                            />
                        )}
                    />
                )}

                {isConsultant && (
                    <Controller
                        name='rv'
                        control={form.control}
                        rules={{ required: _('Seleccioná un RV') }}
                        render={({ field, fieldState: { error } }) => (
                            <MuiSelect
                                inputLabel={_('RV asignado para este productor')}
                                value={field.value}
                                onChange={(e) => field.onChange(e.target.value)}
                                onBlur={field.onBlur}
                                options={rvOptions}
                                formControlProps={{ fullWidth: true, size: 'small', error: !!error }}
                                formHelperTextProps={{ error: !!error }}
                                formHelperText={error?.message}
                                inputProps={{ sx: { background: 'white' } }}
                            />
                        )}
                    />
                )}

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                        gap: 2,
                    }}
                >
                    <Controller
                        name='name'
                        control={form.control}
                        rules={{ required: _('El nombre es requerido') }}
                        render={({ field, fieldState: { error } }) => (
                            <TextField
                                {...field}
                                label={_('Nombre')}
                                fullWidth
                                size='small'
                                error={!!error}
                                helperText={error?.message}
                                inputProps={{ sx: { background: 'white' } }}
                            />
                        )}
                    />
                    <Controller
                        name='location'
                        control={form.control}
                        rules={{ required: _('La ubicación es requerida') }}
                        render={({ field, fieldState: { error } }) => (
                            <TextField
                                {...field}
                                label={_('Ubicación')}
                                fullWidth
                                size='small'
                                error={!!error}
                                helperText={error?.message}
                                inputProps={{ sx: { background: 'white' } }}
                            />
                        )}
                    />
                </Box>

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                        gap: 2,
                    }}
                >
                    <Controller
                        name='email'
                        control={form.control}
                        rules={{
                            required: _('El email es requerido'),
                            validate: (v) =>
                                validateGrowerEmail(
                                    v,
                                    growersQuery.data?.results || [],
                                    ownUserNodeQuery.data,
                                    undefined,
                                    undefined,
                                    isEditMode ? editNodeId : undefined
                                ),
                        }}
                        render={({ field, fieldState: { error } }) => (
                            <TextField
                                {...field}
                                label={_('Email')}
                                type='email'
                                fullWidth
                                size='small'
                                error={!!error}
                                helperText={error?.message}
                                inputProps={{ sx: { background: 'white' } }}
                            />
                        )}
                    />
                    <Controller
                        name='phone'
                        control={form.control}
                        rules={{
                            validate: (v) => {
                                if (!v || v === '') return true;
                                return validatePhoneNumber(v) || _('El formato del número de teléfono es incorrecto.');
                            },
                        }}
                        render={({ field, fieldState }) => (
                            <FormControl fullWidth sx={{ height: 'auto' }}>
                                <PhoneInput
                                    {...field}
                                    onChange={(_value, _data, _event, formattedValue) => {
                                        field.onChange(formattedValue);
                                    }}
                                    buttonStyle={{ border: 'none' }}
                                    specialLabel=''
                                    country={country?.code?.toLowerCase() ?? 'ar'}
                                    inputStyle={{
                                        border: 'none',
                                        borderRadius: '0',
                                        height: 'revert-layer',
                                        width: '100%',
                                    }}
                                    containerStyle={{
                                        border: '1px solid #0000003b',
                                        borderRadius: '0',
                                    }}
                                    enableAreaCodeStretch
                                    enableAreaCodes
                                    areaCodes={AREA_CODES}
                                    masks={{ mx: '... .... ....' }}
                                />
                                <FormHelperText
                                    sx={{
                                        fontSize: 9,
                                        color: fieldState.error ? theme.palette.error.main : theme.palette.primary.main,
                                    }}
                                >
                                    {fieldState.error?.message ?? ''}
                                </FormHelperText>
                            </FormControl>
                        )}
                    />
                </Box>

                {!singleGrower && (
                    <Button
                        type='submit'
                        variant='contained'
                        color='secondary'
                        disabled={!isValid || isSubmitting}
                        sx={{
                            alignSelf: 'flex-start',
                            ...(!isValid && {
                                backgroundColor: 'action.hover',
                                color: 'text.secondary',
                            }),
                        }}
                    >
                        {isSubmitting ? _('Guardando...') : _('Guardar productor')}
                    </Button>
                )}
            </Stack>
        </FormProvider>
    );
};

export default GrowerForm;
